import React, { ReactNode, useCallback } from 'react'
import { useRouter } from 'next/router'
import { firebaseConfig } from '@/config/firebase'
import { clientEncryption, PlatformSDKContextProvider } from '@tmo-apps/platform-sdk'
import { Loader2Icon } from 'lucide-react'
import { IdleTimerProvider } from 'react-idle-timer'
import { useShallow } from 'zustand/react/shallow'

import { promptBeforeIdle, timeout } from '@/constants/expiredTime'
import { useRedirect } from '@/hooks/useRedirect'
import { usePlatformSDKFeature } from '@/hooks/useRemoteConfig'
import { useRouteGuard } from '@/hooks/useRouteGuard'
import { useUserPreferences } from '@/hooks/useUserPreferences'
import { useAuthStore } from '@/store/authStore'
import AppcuesIdentifier from '@/components/AppcuesIdentifier'
import { DataImporterModal } from '@/components/DataImporter'
import DelegateChecker from '@/components/DelegateChecker'
import DialogSessionCountDown from '@/components/DialogSessionCountDown'
import DialogSessionExpired from '@/components/DialogSessionExpired'
import Setup from '@/components/Setup'

type PlatformSDKChildren = React.ComponentProps<typeof PlatformSDKContextProvider>['children']

interface ProtectedPageProps {
  children: ReactNode
}

export function ProtectedPage({ children }: ProtectedPageProps) {
  const router = useRouter()
  const { delegatorId } = useAuthStore((state) => state.permissions)
  const database = useAuthStore((state) => state.database)
  const userToken = useAuthStore((state) => state.userToken)
  const repoId = delegatorId || database?.userId
  const dek = (database?.Encryption.current as any)?.dek || ''
  const dekArrayBuffer = dek ? clientEncryption.base64ToArrayBuffer(dek) : null
  const { platformSDKEnabled } = usePlatformSDKFeature()
  const { preferences } = useUserPreferences()

  const navigateTo = useCallback(
    (url: string) => {
      router.push(url)
    },
    [router]
  )

  const { logout, status, isSetupDone } = useAuthStore(
    useShallow((state) => ({
      logout: state.logout,
      status: state.status,
      isSetupDone: state.isSetupDone
    }))
  )

  useRedirect({
    shouldRedirect: ['unauthenticated', 'closing'].includes(status),
    redirectTo: '/auth/login'
  })
  useRedirect({
    shouldRedirect: status === 'unverified',
    redirectTo: '/auth/verify-email'
  })
  useRouteGuard()

  if (!isSetupDone && !router.pathname.startsWith('/identity')) return <Setup />

  switch (status) {
    case 'sessionExpired': {
      return (
        <>
          {children}
          <DialogSessionExpired />
        </>
      )
    }
    case 'ready': {
      return (
        <IdleTimerProvider
          timeout={timeout}
          promptBeforeIdle={promptBeforeIdle}
          throttle={500}
          onIdle={async () => await logout(true)}
        >
          <>
            {platformSDKEnabled ? (
              <PlatformSDKContextProvider
                firebaseConfig={firebaseConfig}
                encryptionKey={dekArrayBuffer}
                repoUserId={repoId}
                userPreferences={preferences}
                userJWT={userToken}
                navigateTo={navigateTo}
              >
                {children as PlatformSDKChildren}
              </PlatformSDKContextProvider>
            ) : (
              children
            )}
            <DialogSessionCountDown />
            <DataImporterModal />
            <AppcuesIdentifier />
            <DelegateChecker />
          </>
        </IdleTimerProvider>
      )
    }
    default: {
      return (
        <main className={'flex h-screen w-screen items-center justify-center'}>
          <Loader2Icon className={'h-8 w-8 animate-spin text-primary'} />
        </main>
      )
    }
  }
}
